'use client';

import { useCallback } from 'react';
import { ToggleButton, TrackedButton } from '@/components/button';
import Input from '@/components/input';
import searchIcon from '@images/ico_search.svg';
import Image from 'next/image';
import Dropdown from './dropdown';
import {
  useGenePanelDropdown,
  useInputField,
  usePanelToGenesMappingData,
  useSearchMode,
} from '../_hooks';
import { SearchMode } from '../_types';

type SearchInputFieldProps = {
  initialSearchTerm?: string;
  initialSearchMode?: SearchMode;
} & (
  | {
      // 동작하는 검색창: onSearch 필수
      onSearch: (searchInput: string, searchMode: SearchMode) => void;
      disabled?: false;
    }
  | {
      // 하이드레이션 전 fallback: 비활성이라 검색이 없으므로 onSearch 금지
      disabled: true;
      onSearch?: never;
    }
);

/**
 * 유전자 검색을 위한 입력 필드 컴포넌트
 *
 * 검색 모드(Genes/Panel) 전환, 입력 필드, 드롭다운 기능을 포함
 */
function SearchInputField({
  onSearch = () => {},
  initialSearchTerm = '',
  initialSearchMode = 'Genes',
  disabled = false,
}: SearchInputFieldProps) {
  // 패널-유전자 매핑 데이터 가져오기
  const { data: panelToGenesMappingData = {} } = usePanelToGenesMappingData();

  // 검색 모드 관리 (Genes/Panel) — URL 모드로 초기화·동기화
  const { searchMode, setSearchMode, placeholder } =
    useSearchMode(initialSearchMode);

  // 검색 실행 콜백 (현재 모드를 함께 전달)
  const handleSearch = useCallback(
    (originalInput: string) => onSearch(originalInput, searchMode),
    [onSearch, searchMode],
  );

  // 입력 필드 관리
  const { inputValue, setInputValue, handleKeyDown, handleSearchClick } =
    useInputField(handleSearch, initialSearchTerm);

  // 드롭다운 관리
  const {
    isDropdownVisible,
    genesIncludedInPanel,
    inputRef,
    handleItemSelect,
    handleDropdownVisibility,
  } = useGenePanelDropdown(
    inputValue,
    setInputValue,
    searchMode,
    panelToGenesMappingData,
    handleSearch,
  );

  // 입력 변경 핸들러 (입력값 업데이트 + 드롭다운 표시 설정)
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      handleDropdownVisibility();
    },
    [setInputValue, handleDropdownVisibility],
  );

  // 모드 변경 핸들러
  const handleModeChange = useCallback(
    (mode: SearchMode) => {
      setSearchMode(mode);
      setInputValue('');
    },
    [setSearchMode, setInputValue],
  );

  return (
    <div className="relative w-full" ref={inputRef}>
      <Input
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleDropdownVisibility}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className="p-3"
        inputClassName="-m-3 p-3 pl-4"
        leftNode={
          <ToggleButton
            currentValue={searchMode}
            setCurrentValue={handleModeChange}
            leftValue="Genes"
            rightValue="Panel"
            disabled={disabled}
          />
        }
        rightNode={
          <TrackedButton
            label="search_button"
            position="main_content"
            type="submit"
            disabled={disabled}
            className="hidden aspect-square size-8 items-center justify-center rounded-md px-[6px] hover:bg-3b-background-medium md:flex"
            onClick={handleSearchClick}
          >
            <Image src={searchIcon} alt="검색" priority />
          </TrackedButton>
        }
      />
      {isDropdownVisible && (
        <Dropdown
          items={genesIncludedInPanel}
          onItemSelect={handleItemSelect}
          isOpen={inputValue.length > 0}
        />
      )}
    </div>
  );
}

export default SearchInputField;
